import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import KeyResultCardHeader from './KeyResultCardHeader';
import KeyResultMeasurementFields from './KeyResultMeasurementFields';
import KeyResultProgressRow from './KeyResultProgressRow';
import { defaultsForUnit } from '../validation';

import type { EmployeeOption } from '../hooks/useKeyResultResponsibles';
import type { ObjectiveFormData } from '../validation';
import type { KeyResultUnitValue } from '@/constants/KeyResultUnit';
import type { Control, UseFormSetValue } from 'react-hook-form';

import { KeyResultUnit } from '@/constants/KeyResultUnit';
import i18n from '@/plugins/i18n';
import {
    clampPercent,
    clampToKeyResultRange,
    computeKeyResultDisplayPercentage,
    computeKeyResultValueFromPercentage,
} from '@/utils/objectiveProgress';
import { formatKeyResultTarget } from '@/utils/objectivesFormat';

interface KeyResultFieldsProps {
    control: Control<ObjectiveFormData>;
    setValue: UseFormSetValue<ObjectiveFormData>;
    index: number;
    onRemove: () => void;
    canRemove: boolean;
    employeeOptions: EmployeeOption[];
    isTeam: boolean;
}

export default function KeyResultFields({
    control,
    setValue,
    index,
    onRemove,
    canRemove,
    employeeOptions,
    isTeam,
}: KeyResultFieldsProps) {
    const { t } = useTranslation();
    const lang = i18n.language;
    const keyResult = useWatch({ control, name: `keyResults.${index}` });
    const unit = keyResult?.unit as KeyResultUnitValue | undefined;
    const isYesNo = unit === KeyResultUnit.YES_NO;

    // Values only reset when the row crosses the Yes/No boundary, where the numbers change
    // meaning; between two numeric units a typed target survives.
    const handleUnitChange = (nextUnit: string, onChange: (value: string) => void) => {
        onChange(nextUnit);
        if (isYesNo === (nextUnit === KeyResultUnit.YES_NO)) {
            return;
        }
        const defaults = defaultsForUnit(nextUnit as KeyResultUnitValue);
        setValue(`keyResults.${index}.startValue`, defaults.startValue);
        setValue(`keyResults.${index}.targetValue`, defaults.targetValue);
        setValue(`keyResults.${index}.currentValue`, defaults.currentValue);
    };

    // A current value nobody has moved yet still sits on the start: it follows the start, so a
    // key result aiming below its start does not open already past its target.
    const handleStartChange = (nextStart: number, onChange: (value: number) => void) => {
        if (keyResult?.currentValue === keyResult?.startValue) {
            setValue(`keyResults.${index}.currentValue`, nextStart);
        }
        onChange(nextStart);
    };

    // Badge and progress bar are DISPLAY only, computed client-side from the row's own
    // start/target/current -- the backend never sees or stores this value (progress.ts).
    const displayPercentage = clampPercent(
        unit
            ? computeKeyResultDisplayPercentage({
                  unit,
                  startValue: keyResult?.startValue ?? 0,
                  targetValue: keyResult?.targetValue ?? 0,
                  currentValue: keyResult?.currentValue ?? 0,
              })
            : 0,
    );

    // The bar's own inverse of the calculation above: dragging or clicking it reports a
    // percentage, which is converted back into the row's `currentValue` -- the same math
    // `computeKeyResultDisplayPercentage` runs, in reverse (progress.ts).
    const handleBarChange = (nextPercentage: number) => {
        setValue(
            `keyResults.${index}.currentValue`,
            computeKeyResultValueFromPercentage({
                unit: unit ?? KeyResultUnit.NUMBER,
                startValue: keyResult?.startValue ?? 0,
                targetValue: keyResult?.targetValue ?? 0,
                percentage: nextPercentage,
            }),
            { shouldDirty: true },
        );
    };

    // The current value never leaves [start, target]: stepping stops at either end, and a typed
    // value is brought back into range once the field is left, so typing is never interrupted.
    const clampCurrentValue = (value: number): number =>
        clampToKeyResultRange({
            startValue: keyResult?.startValue ?? 0,
            targetValue: keyResult?.targetValue ?? 0,
            value,
        });

    return (
        <div
            className={
                'group/card relative flex flex-col rounded-[16px] border border-white bg-white/90 p-5 backdrop-blur-[2px]'
            }
            data-testid={`key-result-row-${index}`}
        >
            <KeyResultCardHeader
                control={control}
                index={index}
                unit={unit}
                isTeam={isTeam}
                employeeOptions={employeeOptions}
                canRemove={canRemove}
                onRemove={onRemove}
                displayPercentage={displayPercentage}
                language={lang}
            />

            <KeyResultMeasurementFields
                control={control}
                index={index}
                isYesNo={isYesNo}
                isMonetary={unit === KeyResultUnit.MONETARY}
                onUnitChange={handleUnitChange}
                onStartChange={handleStartChange}
            />

            <KeyResultProgressRow
                control={control}
                index={index}
                isYesNo={isYesNo}
                displayPercentage={displayPercentage}
                onPercentageChange={handleBarChange}
                clampValue={clampCurrentValue}
                barLabel={keyResult?.title || t('objectives.form.keyResultNamePlaceholder')}
                targetLabel={`/${formatKeyResultTarget(
                    keyResult?.targetValue ?? 0,
                    unit,
                    keyResult?.currencySymbol,
                    lang,
                )}`}
            />
        </div>
    );
}
